import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import styled from '@emotion/styled';
import Paper from '@base/Paper/Paper';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { goBack } from '@utils/breadcrumbs';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import { fetchBatchesGroupTrailClasses } from '@actions/batchGroups';
import { useRouter } from 'next/router';
import { useDispatchToStore, useSelectorToStore } from '@hooks/reduxHooks';
import PlusCourseCard from '@comp/PlusCourseCard/PlusCourseCard';
import { getBatchGroupFreeClasses } from '@selectors/batchGroups';
import { getImage } from '@utils/imageHelper';
import EmptyState from '@comp/EmptyState/EmptyState';

const Container = styled.div`
  display: grid;
  grid-column: span 24;
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const TopBar = styled(Paper)`
  display: flex;
  padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    width: auto;
    width: 100%;
    box-shadow: none;
    padding: ${Spacings.SPACING_6B} 0;
    background-color: var(--color-base-0);
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const Title = styled.div`
  margin-left: ${Spacings.SPACING_3B};
  color: var(--color-text-primary);
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.H4};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_0B};
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const ListContainer = styled.div`
  width: 1136px;
  margin: ${Spacings.SPACING_10B} auto;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-row-gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin: ${Spacings.SPACING_6B} auto;
    width: 100%;
  }
`;

const ClassCard = styled(PlusCourseCard)`
  width: 272px;
  margin-bottom: ${Spacings.SPACING_8B};
  .batch-thumbnail {
    width: 100%;
    height: 152px;
  }
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_0B};
    width: 100%;
  }
`;

const StyledEmptyState = styled(EmptyState)`
  grid-template-columns: repeat(18, 32px);
  grid-column: 1/19;
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  height: 384px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  margin-top: ${Spacings.SPACING_10B};
  h3 {
    font-size: ${Fonts.LARGE};
  }
  p {
    font-size: ${Fonts.P2};
    color: var(--color-text-secondary);
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: 290px;
    img {
      height: 90px;
      width: 90px;
    }
    h3 {
      margin-top: ${Spacings.SPACING_4B};
      margin-bottom: ${Spacings.SPACING_2B};
    }
  }
`;

const renderTrailClasses = (trialClass) => {
  return (
    <ClassCard
      key={trialClass.uid}
      course={trialClass}
      disabledTopicGroup
      hideLessonCount
    />
  );
};
const BatchesGroupClassesListing = () => {
  const router = useRouter();
  const dispatch = useDispatchToStore();
  const { goalUID } = router.query;
  const loadMore = (resetWithoutEmpty, forceReset) => {
    return dispatch(
      fetchBatchesGroupTrailClasses(
        goalUID,
        true,
        resetWithoutEmpty,
        forceReset
      )
    );
  };
  const batchGroupFreeClasses = useSelectorToStore(getBatchGroupFreeClasses);
  const data = batchGroupFreeClasses?.[
    `${goalUID}-batch-group-free-classes`
  ] || { results: [] };
  const hasNoTrailClasses = data?.results?.length === 0;
  return (
    <Container>
      <TopBar gridColumn="span 18">
        <BreadCrumbs tags={[]} onBackClick={goBack} />
        <Title gridColumn="span 18">Demo classes from batches</Title>
      </TopBar>
      {!hasNoTrailClasses && (
        <ListContainer>
          <InfiniteList
            data={data}
            loadMore={loadMore}
            _renderItem={renderTrailClasses}
            loaderType="course"
          />
        </ListContainer>
      )}
      {!data?.isFetching && hasNoTrailClasses && (
        <StyledEmptyState
          imgSrc={getImage('batch-group/no-free-classes.svg')}
          title="No demo classes available"
          description="Demo classes from batches will show up here"
        />
      )}
    </Container>
  );
};
export default BatchesGroupClassesListing;
